import * as React from 'react';
import { MdOpenInNew } from 'react-icons/md';
import Link from 'components/Link';
import { FlatButton } from 'styles/elements';
import Pulse from 'components/Loading/Pulse';
import { StrategyMatric } from 'screens/OptionsProfitCalculator/StrategyMatric';
import { StrategyGreeks } from 'screens/OptionsProfitCalculator/StrategyGreeks';
import { MemoizedChart } from 'screens/OptionsProfitCalculator/MemoizedChart';
import PayoffTable from './PayoffTable';
import { PayoffSectionProps } from './types';
import { CallPut } from 'utils/shared/trades/types';
import { normalizeCallPut } from 'utils/shared/trades/normalizeCallPut';
import {
  getOptionFlowStrategyPosition,
  OptionFlowStrategyPosition,
} from 'utils/shared/trades/getOptionFlowRowSentiment';

const PayoffSection: React.FC<PayoffSectionProps> = ({
  ticker,
  calculationData,
  spot,
  strike,
  premium,
  volatility,
  trade,
  loading = false,
}) => {
  const strategyPosition = getOptionFlowStrategyPosition(trade);
  const isStrategyNeutral = strategyPosition === OptionFlowStrategyPosition.Neutral;

  const getOptionsStrategyUrl = () => {
    if (!trade || isStrategyNeutral) return '#';

    const positionPrefix = strategyPosition === OptionFlowStrategyPosition.Long ? 'long' : 'short';
    const optionType = normalizeCallPut(trade.cp) === CallPut.Put ? 'put' : 'call';
    const strategy = `${positionPrefix}-${optionType}`;
    const expiration =
      trade.expireYear && trade.expireMonth && trade.expireDay
        ? `${trade.expireYear}-${String(trade.expireMonth).padStart(2, '0')}-${String(
            trade.expireDay,
          ).padStart(2, '0')}`
        : '';

    const size = trade?.size || 1;
    const queryParams = new URLSearchParams();
    if (expiration) {
      queryParams.set('expiration', expiration);
    }
    queryParams.set('strike', String(strike));
    queryParams.set('size', String(size));

    return `/options-profit-calculator/strategy/${strategy}/${ticker}?${queryParams.toString()}`;
  };

  return (
    <section className="mb-8 md:mb-12">
      <div
        id="section-payoff-graph"
        className="mb-3 flex flex-col gap-2 md:mb-4 md:flex-row md:items-end"
      >
        <h2 className="mb-1 mr-2 text-2xl font-semibold md:text-3xl">{ticker} Payoff</h2>
        {!isStrategyNeutral && (
          <div className="hidden md:block">
            <Link href={getOptionsStrategyUrl()} isExternal>
              <FlatButton className="mt-1">
                Open Options Strategy
                <MdOpenInNew />
              </FlatButton>
            </Link>
          </div>
        )}
      </div>
      {calculationData?.strategyValues && (
        <>
          <StrategyMatric
            breakEvenPoints={calculationData.breakEvenPoints || []}
            strategyValues={calculationData.strategyValues}
            normalizedProfitFactor={calculationData.normalizedProfitFactor || 0}
          />
          <StrategyGreeks strategyValues={calculationData.strategyValues} volatility={volatility} />
        </>
      )}
      <div
        className="relative mt-2 w-full rounded-lg bg-palette-black-4 p-3 md:p-6"
        style={{ minHeight: '350px' }}
      >
        {isStrategyNeutral ? (
          <div className="flex h-full items-center justify-center">
            <div className="max-w-md text-center">
              <p className="text-sm font-semibold text-gray-200">No payoff direction inferred</p>
              <p className="mt-2 text-xs leading-relaxed text-gray-400">
                This trade printed at the midpoint, so the app shows it as neutral instead of
                projecting a long or short strategy.
              </p>
            </div>
          </div>
        ) : !spot || !strike || !premium || loading ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <Pulse />
              <p className="mt-4 text-gray-400">Loading trade data...</p>
            </div>
          </div>
        ) : (
          <MemoizedChart
            payoffGraph={calculationData?.payoffGraph}
            maxProfitLoss={calculationData?.maxProfitLoss}
            breakEvenPoints={calculationData?.breakEvenPoints}
            probabilityData={calculationData?.probabilityData}
            spot={calculationData?.spot}
          />
        )}
      </div>
      {!isStrategyNeutral && (
        <div id="section-payoff-table">
          <PayoffTable payoffTable={calculationData?.payoffTable} />
        </div>
      )}
    </section>
  );
};

export default PayoffSection;
